import './WholesaleSalePrintModal.css';

import type { DashboardWholesaleSale } from '@pos/types';
import { useEffect } from 'react';

import { Btn } from '../../components/Btn';
import { Modal } from '../../components/Modal';
import { fmtAmount } from '../../lib/format';
import type { SystemSettings } from '../../types/domain';

const fmtDocumentDate = (value: string): string =>
  new Intl.DateTimeFormat('th-TH-u-nu-latn', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(new Date(value));

function SaleDocument({
  sale,
  branchName,
  settings,
}: {
  sale: DashboardWholesaleSale;
  branchName: string;
  settings: SystemSettings;
}) {
  const customer = sale.customer;
  const customerHasDetails =
    customer &&
    (customer.name || customer.taxId || customer.phone || customer.address);

  return (
    <article
      aria-label="ตัวอย่างบิลขายเงินสด A4"
      className="wholesale-sale-print-root box-border flex min-h-[297mm] w-[210mm] flex-col bg-white p-[12mm] text-[11px] leading-relaxed text-slate-950 shadow-lg ring-1 ring-slate-200"
    >
      <header className="grid grid-cols-[1fr_auto] items-start gap-8 border-b-2 border-slate-900 pb-3">
        <div>
          <div className="text-[17px] font-bold">
            {settings.shopName || 'ร้านค้า'}
          </div>
          <div className="mt-1 text-[10px] text-slate-600">{branchName}</div>
          {settings.taxId && (
            <div className="text-[10px] text-slate-600">
              เลขประจำตัวผู้เสียภาษี {settings.taxId}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold tracking-[0.12em]">
            CASH SALES / DELIVERY
          </div>
          <h2 className="text-[22px] font-bold">บิลขายเงินสด / ใบส่งสินค้า</h2>
          <dl className="mt-2 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
            <dt className="text-slate-500">เลขที่</dt>
            <dd className="font-bold tabular-nums">{sale.receiptNo}</dd>
            <dt className="text-slate-500">วันที่</dt>
            <dd className="tabular-nums">{fmtDocumentDate(sale.soldAt)}</dd>
          </dl>
        </div>
      </header>

      <section className="mt-4 min-h-[76px] border border-slate-400 p-3">
        <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-slate-500">
          ลูกค้า / Customer
        </div>
        {customerHasDetails ? (
          <div className="grid grid-cols-2 gap-x-8 gap-y-1">
            <div className="font-bold">{customer?.name || '-'}</div>
            <div className="text-right">
              {customer?.taxId ? `เลขผู้เสียภาษี ${customer.taxId}` : '\u00a0'}
            </div>
            <div className="whitespace-pre-line">
              {customer?.address || '-'}
            </div>
            <div className="text-right">
              {customer?.phone ? `โทร ${customer.phone}` : '\u00a0'}
            </div>
          </div>
        ) : (
          <div className="text-[14px] font-bold">ลูกค้าทั่วไป</div>
        )}
      </section>

      <table className="mt-5 w-full table-fixed border-collapse">
        <thead>
          <tr className="border-y-2 border-slate-900 text-left">
            <th className="w-10 py-2 text-center">ลำดับ</th>
            <th className="py-2">รายการสินค้า</th>
            <th className="w-20 py-2 text-right">จำนวน</th>
            <th className="w-20 py-2">หน่วย</th>
            <th className="w-24 py-2 text-right">ราคา/หน่วย</th>
            <th className="w-24 py-2 text-right">จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {sale.lines.map((line, index) => (
            <tr
              key={`${line.productId}:${line.unitId}:${index}`}
              className="border-b border-slate-300"
            >
              <td className="py-2 text-center tabular-nums">{index + 1}</td>
              <td className="py-2 pr-2 font-semibold">{line.productName}</td>
              <td className="py-2 text-right tabular-nums">
                {line.quantity.toLocaleString('en-US')}
              </td>
              <td className="py-2">{line.unitName}</td>
              <td className="py-2 text-right tabular-nums">
                {fmtAmount(line.unitPrice)}
              </td>
              <td className="py-2 text-right font-semibold tabular-nums">
                {fmtAmount(line.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 grid grid-cols-[1fr_250px] gap-8">
        <section className="min-h-24">
          <div className="text-slate-500">หมายเหตุ</div>
          <p className="whitespace-pre-line">{sale.note || '-'}</p>
        </section>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
          <dt>รวมค่าสินค้า</dt>
          <dd className="text-right tabular-nums">
            {fmtAmount(sale.totalAmount)}
          </dd>
          <dt>ชำระเงินสดแล้ว</dt>
          <dd className="text-right tabular-nums">
            {fmtAmount(sale.totalAmount)}
          </dd>
          <dt className="border-t-2 border-slate-900 pt-2 text-[14px] font-bold">
            ยอดสุทธิ
          </dt>
          <dd className="border-t-2 border-slate-900 pt-2 text-right text-[14px] font-bold tabular-nums">
            {fmtAmount(sale.totalAmount)} บาท
          </dd>
        </dl>
      </div>

      <div className="mt-3 border border-slate-400 bg-slate-50 px-3 py-2 text-center font-bold">
        ได้รับชำระเงินสดครบถ้วนแล้ว
      </div>

      <footer className="mt-auto grid grid-cols-2 gap-16 pt-16 text-center">
        <div>
          <div className="border-b border-slate-600 pb-8" />
          <div className="pt-2">ผู้รับสินค้า / ผู้ชำระเงิน</div>
          <div>วันที่ ______ / ______ / ______</div>
        </div>
        <div>
          <div className="border-b border-slate-600 pb-8" />
          <div className="pt-2">ผู้ส่งสินค้า / ผู้รับเงิน</div>
          <div>วันที่ ______ / ______ / ______</div>
        </div>
      </footer>
    </article>
  );
}

export function WholesaleSalePrintModal({
  open,
  sale,
  branchName,
  settings,
  onClose,
}: {
  open: boolean;
  sale: DashboardWholesaleSale | null;
  branchName: string;
  settings: SystemSettings;
  onClose: () => void;
}) {
  useEffect(
    () => () => document.body.classList.remove('wholesale-sale-printing'),
    [],
  );

  if (!sale) return null;

  const print = () => {
    document.body.classList.add('wholesale-sale-printing');
    try {
      window.print();
    } finally {
      document.body.classList.remove('wholesale-sale-printing');
    }
  };

  return (
    <Modal
      open={open}
      title="บันทึกยอดขายสำเร็จ"
      onClose={onClose}
      className="wholesale-sale-dialog w-[min(980px,96vw)]"
    >
      <div className="wholesale-sale-screen-only">
        <div className="mb-4 flex flex-col gap-3 rounded-card border border-success/30 bg-success/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-bold text-success">
              รับเงินสดและตัดสต็อกเรียบร้อยแล้ว
            </div>
            <div className="text-sm-label text-ink-3">
              เลขที่ {sale.receiptNo} · {fmtAmount(sale.totalAmount)} บาท
            </div>
          </div>
          <div className="flex gap-2">
            <Btn type="button" onClick={onClose}>
              ปิด
            </Btn>
            <Btn kind="primary" type="button" onClick={print}>
              พิมพ์ A4
            </Btn>
          </div>
        </div>
        <div className="wholesale-sale-preview-frame overflow-auto rounded-card bg-bg-warm p-4">
          <SaleDocument
            sale={sale}
            branchName={branchName}
            settings={settings}
          />
        </div>
      </div>
    </Modal>
  );
}
